import {
	ChangeDetectionStrategy,
	Component,
	effect,
	HostListener,
	input,
	OnDestroy,
	output,
	signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import {
	CdkDragDrop,
	DragDropModule,
	moveItemInArray,
} from '@angular/cdk/drag-drop';
import { JeptoSlideToggleComponent } from '@framework/ui-lib/form-controls/jepto-slide-toggle/jepto-slide-toggle.component';
import { JeptoUiInputComponent } from '@framework/ui-lib/form-controls/jepto-ui-input/jepto-ui-input.component';
import {
	getDefaultNextPage,
	UNTITLED_PAGE_LABEL,
	type PageEditorPage,
} from '../page-editor.model';

/** Debounce label typing before notifying the host (store serializes server work). */
const LABEL_PAGES_CHANGE_DEBOUNCE_MS = 400;

@Component({
	selector: 'jepto-page-editor-manage-pages',
	standalone: true,
	imports: [
		CommonModule,
		FormsModule,
		MatIconModule,
		JeptoSlideToggleComponent,
		JeptoUiInputComponent,
		DragDropModule,
	],
	templateUrl: './jepto-page-editor-manage-pages.component.html',
	styleUrl: './jepto-page-editor-manage-pages.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	host: {
		'[class.jepto-page-editor-manage-pages--open]': 'open()',
		'[attr.aria-hidden]': 'open() ? null : true',
	},
})
export class JeptoPageEditorManagePagesComponent implements OnDestroy {
	/** Current list of pages (from parent). */
	readonly pages = input.required<PageEditorPage[]>();
	/** When true, the panel is visible. */
	readonly open = input<boolean>(false);
	/** When true, tabs are shown in preview mode. Default true. */
	readonly tabsEnabledInPreview = input<boolean>(true);

	/** Emitted with the validated full list whenever page rows change (live apply; host persists). */
	readonly pagesChange = output<PageEditorPage[]>();
	/** Emitted when the user chooses Delete on a row; host confirms and calls `deletePage`. */
	readonly pageDeleteRequest = output<string>();
	/** Emitted when the user dismisses the panel (X). Blank page names commit as `UNTITLED_PAGE_LABEL` on close; other edits apply live via `pagesChange`. */
	readonly closePanel = output<void>();
	/** Emitted when **Show Page Tabs** changes; fires immediately with the new value (live apply). */
	readonly tabsEnabledInPreviewChange = output<boolean>();

	/** Working copy for the list (synced from pages input when panel opens). */
	readonly workingPages = signal<PageEditorPage[]>([]);

	#labelPagesChangeTimer: ReturnType<typeof setTimeout> | null = null;
	/** Page ids with in-progress label edits; plain Set (not a signal) to avoid re-running the pages-sync effect on every keystroke. */
	#dirtyLabelPageIds = new Set<string>();
	#panelSyncedFromParent = false;
	/** Working copy of tabs enabled in preview. */
	readonly workingTabsEnabled = signal<boolean>(true);
	/** Index of row whose inline row menu is open; -1 when none. */
	readonly openRowMenuIndex = signal<number>(-1);

	constructor() {
		effect(() => {
			const p = this.pages();
			const isOpen = this.open();
			if (!isOpen) {
				this.openRowMenuIndex.set(-1);
				this.#dirtyLabelPageIds.clear();
				this.#panelSyncedFromParent = false;
				return;
			}
			if (!this.#panelSyncedFromParent) {
				this.workingPages.set(p.map(page => ({ ...page })));
				this.workingTabsEnabled.set(this.tabsEnabledInPreview());
				this.#panelSyncedFromParent = true;
				return;
			}
			this.syncWorkingPagesFromParent(p);
		});
	}

	@HostListener('document:click', ['$event'])
	onDocumentClick(event: MouseEvent): void {
		if (this.openRowMenuIndex() < 0) return;
		const target = event.target as HTMLElement | null;
		if (target?.closest('.jepto-page-editor-manage-pages__menu-wrap')) {
			return;
		}
		this.openRowMenuIndex.set(-1);
	}

	@HostListener('document:keydown', ['$event'])
	onDocumentKeydown(event: KeyboardEvent): void {
		if (event.key !== 'Escape') return;
		if (this.openRowMenuIndex() < 0) return;
		event.preventDefault();
		this.openRowMenuIndex.set(-1);
	}

	setPageLabel(index: number, value: string): void {
		const list = this.workingPages();
		const pageId = list[index]?.id;
		if (!pageId) {
			return;
		}
		if (list[index]?.label === value) {
			return;
		}
		this.markLabelDirty(pageId);
		this.workingPages.set(
			list.map((p, i) => (i === index ? { ...p, label: value } : p))
		);
		this.scheduleLabelPagesChangeEmit();
	}

	onPageInputIconClick(index: number, pos: 'prefix' | 'suffix'): void {
		if (pos === 'suffix') {
			const page = this.workingPages()[index];
			if (page?.hidden) {
				this.setHidden(index, false);
			}
		}
	}

	onDrop(event: CdkDragDrop<PageEditorPage[]>): void {
		if (event.previousIndex === event.currentIndex) return;
		const list = [...this.workingPages()];
		moveItemInArray(list, event.previousIndex, event.currentIndex);
		this.workingPages.set(list);
		this.emitPagesChangeNow();
	}

	setHidden(index: number, hidden: boolean): void {
		const list = this.workingPages().map((p, i) =>
			i === index ? { ...p, hidden } : p
		);
		this.workingPages.set(list);
		this.emitPagesChangeNow();
	}

	onTabsEnabledChange(checked: boolean): void {
		this.workingTabsEnabled.set(checked);
		this.tabsEnabledInPreviewChange.emit(checked);
	}

	onAddPage(): void {
		const list = this.workingPages();
		const nextPage = getDefaultNextPage(list);
		this.workingPages.set([...list, nextPage]);
		this.emitPagesChangeNow();
	}

	toggleRowMenu(index: number, event: MouseEvent): void {
		event.stopPropagation();
		this.openRowMenuIndex.update(current =>
			current === index ? -1 : index
		);
	}

	onRowMenuHideClick(index: number): void {
		if (index < 0) return;
		const page = this.workingPages()[index];
		if (page) {
			this.setHidden(index, !page.hidden);
		}
		this.openRowMenuIndex.set(-1);
	}

	onRowMenuDeleteClick(index: number): void {
		const list = this.workingPages();
		if (list.length <= 1 || index < 0 || index >= list.length) {
			return;
		}
		const page = list[index];
		if (!page) {
			return;
		}
		this.openRowMenuIndex.set(-1);
		this.pageDeleteRequest.emit(page.id);
	}

	private markLabelDirty(pageId: string): void {
		this.#dirtyLabelPageIds.add(pageId);
	}

	private clearAllLabelDirty(): void {
		this.#dirtyLabelPageIds.clear();
	}

	/** After a live emit, keep blank or untrimmed in-progress labels dirty so parent sync does not overwrite them. */
	private clearDirtyLabelsAfterLiveEmit(): void {
		for (const page of this.workingPages()) {
			if (!this.#dirtyLabelPageIds.has(page.id)) {
				continue;
			}
			const label = page.label ?? '';
			if (label.length > 0 && label === label.trim()) {
				this.#dirtyLabelPageIds.delete(page.id);
			}
		}
	}

	private syncWorkingPagesFromParent(pages: PageEditorPage[]): void {
		const dirty = this.#dirtyLabelPageIds;
		const prev = this.workingPages();
		const next = pages.map(page => {
			if (!dirty.has(page.id)) {
				return { ...page };
			}
			const local = prev.find(row => row.id === page.id);
			return local ? { ...page, label: local.label } : { ...page };
		});
		if (
			prev.length === next.length &&
			prev.every(
				(row, i) =>
					row.id === next[i]?.id &&
					row.label === next[i]?.label &&
					row.hidden === next[i]?.hidden &&
					row.icon === next[i]?.icon
			)
		) {
			return;
		}
		this.workingPages.set(next);
	}

	/** Title sent on live emits (debounce / reorder / hide). Blank local edits keep the parent title. */
	private resolveLabelForLiveEmit(page: PageEditorPage): string {
		const trimmed = page.label?.trim() ?? '';
		if (trimmed.length > 0) {
			return trimmed;
		}
		const parentRow = this.pages().find(p => p.id === page.id);
		return parentRow?.label?.trim() ?? '';
	}

	private buildLiveEmitPages(): PageEditorPage[] {
		return this.workingPages().map(p => ({
			...p,
			label: this.resolveLabelForLiveEmit(p),
		}));
	}

	private buildCommittedPages(): PageEditorPage[] {
		return this.workingPages().map(p => ({
			...p,
			label: p.label?.trim() || UNTITLED_PAGE_LABEL,
		}));
	}

	private hasPagesChangeSinceParent(committed: PageEditorPage[]): boolean {
		const parent = this.pages();
		if (parent.length !== committed.length) {
			return true;
		}
		return committed.some(row => {
			const prev = parent.find(p => p.id === row.id);
			if (!prev) {
				return true;
			}
			return (
				row.label !== prev.label ||
				(row.hidden ?? false) !== (prev.hidden ?? false) ||
				row.icon !== prev.icon
			);
		});
	}

	/** Pushes the current working list to the host (live labels; blank rows keep parent title). */
	private emitPagesChange(): void {
		if (!this.open()) return;
		this.pagesChange.emit(this.buildLiveEmitPages());
	}

	/** Cancels any pending label debounce, then emits (reorder, hide, add). */
	private emitPagesChangeNow(): void {
		if (this.#labelPagesChangeTimer) {
			clearTimeout(this.#labelPagesChangeTimer);
			this.#labelPagesChangeTimer = null;
		}
		this.emitPagesChange();
	}

	/** Label edits debounce; structural edits emit immediately. Blank labels do not emit. */
	private scheduleLabelPagesChangeEmit(): void {
		if (!this.open()) return;
		const hasNonBlankDirtyLabel = this.workingPages().some(
			p =>
				this.#dirtyLabelPageIds.has(p.id) &&
				(p.label?.trim() ?? '').length > 0
		);
		if (!hasNonBlankDirtyLabel) {
			if (this.#labelPagesChangeTimer) {
				clearTimeout(this.#labelPagesChangeTimer);
				this.#labelPagesChangeTimer = null;
			}
			return;
		}
		if (this.#labelPagesChangeTimer) {
			clearTimeout(this.#labelPagesChangeTimer);
		}
		this.#labelPagesChangeTimer = setTimeout(() => {
			this.#labelPagesChangeTimer = null;
			this.emitPagesChange();
			this.clearDirtyLabelsAfterLiveEmit();
		}, LABEL_PAGES_CHANGE_DEBOUNCE_MS);
	}

	ngOnDestroy(): void {
		if (this.#labelPagesChangeTimer) {
			clearTimeout(this.#labelPagesChangeTimer);
			this.#labelPagesChangeTimer = null;
		}
		this.#dirtyLabelPageIds.clear();
	}

	onCancel(): void {
		if (this.#labelPagesChangeTimer) {
			clearTimeout(this.#labelPagesChangeTimer);
			this.#labelPagesChangeTimer = null;
		}
		const committed = this.buildCommittedPages();
		this.workingPages.set(committed.map(p => ({ ...p })));
		if (this.hasPagesChangeSinceParent(committed)) {
			this.pagesChange.emit(committed);
		}
		this.clearAllLabelDirty();
		this.closePanel.emit();
	}

	trackByPageId(_index: number, page: PageEditorPage): string {
		return page.id;
	}
}
